import "./App.css";
import { Suspense } from "react";
import { lazyWithRetry } from "./lib/lazyWithRetry.js";
import { Navigate, Routes, Route, useLocation } from "react-router-dom";
import Navbar, { NAVBAR_TOOL_ROUTES } from "./components/Navbar.jsx";
import UpdateNotice from "./components/UpdateNotice.jsx";
import CommandPalette from "./components/CommandPalette.jsx";
import Footer from "./components/Footer.jsx";
import { HACKER_PORTAL_PATH } from "./constants/routes.js";

// Eager: only the tiny shared/fallback pages. LoginPage and AuthCallback used
// to be eager so the magic-link flow ran without a second chunk download, but
// they statically pull the whole firebase auth+firestore SDK into the main
// bundle for every visitor. One extra chunk fetch on the two auth routes is
// the better trade.
import NotFoundPage from "./components/NotFoundPage.jsx";
import UnauthorizedPage from "./components/UnauthorizedPage.jsx";
import ApplyClosedPage from "./components/ApplyClosedPage.jsx";

// Lazy: everything heavy or off the sign-in path. Each becomes its own chunk,
// so a cold load (especially the magic-link callback) ships far less JS.
const V2PreviewPage = lazyWithRetry(() => import("./components/V2PreviewPage.jsx"));
const LoginPage = lazyWithRetry(() => import("./components/LoginPage.jsx"));
const AuthCallback = lazyWithRetry(() => import("./components/AuthCallback.jsx"));
const SponsorshipPage = lazyWithRetry(() => import("./components/SponsorshipPage.jsx"));
const importSponsorsPage = () => import("./components/SponsorsPage.jsx");
const SponsorsPage = lazyWithRetry(importSponsorsPage);
const importTracksPage = () => import("./components/TracksV2Page.jsx");
const TracksPage = lazyWithRetry(importTracksPage);
const CareerFairPartnersPage = lazyWithRetry(() => import("./components/CareerFairPartnersPage.jsx"));
const ApplyPage = lazyWithRetry(() => import("./components/ApplyPage.jsx"));
const StaffDashboard = lazyWithRetry(() => import("./components/StaffDashboard.jsx"));
const importSponsorPerksPage = () => import("./components/SponsorPerksPage.jsx");
const SponsorPerksPage = lazyWithRetry(importSponsorPerksPage);
const QRScanner = lazyWithRetry(() => import("./components/QRScanner.jsx"));
const StaffTeamsPage = lazyWithRetry(() => import("./components/StaffTeamsPage.jsx"));
const StaffMetricsPage = lazyWithRetry(() => import("./components/StaffMetricsPage.jsx"));
const StaffConsole = lazyWithRetry(() => import("./components/StaffConsole.jsx"));
const StaffOutboxPage = lazyWithRetry(() => import("./components/StaffOutboxPage.jsx"));
const CsvExportPage = lazyWithRetry(() => import("./components/CsvExportPage.jsx"));
const ReferrerLeaderboard = lazyWithRetry(() => import("./components/ReferrerLeaderboard.jsx"));
const JudgingPage = lazyWithRetry(() => import("./components/JudgingPage.jsx"));
const ReviewPage = lazyWithRetry(() => import("./components/ReviewPage.jsx"));
const HackerDashboardLayout = lazyWithRetry(() => import("./components/HackerDashboardLayout.jsx"));
const importMyApplicationPage = () => import("./components/MyApplicationPage.jsx");
const MyApplicationPage = lazyWithRetry(importMyApplicationPage);
const importQRCodePage = () => import("./components/QRCodePage.jsx");
const QRCodePage = lazyWithRetry(importQRCodePage);
const importTeamsPage = () => import("./components/TeamsPage.jsx");
const TeamsPage = lazyWithRetry(importTeamsPage);
const importMyTeamPage = () => import("./components/MyTeamPage.jsx");
const MyTeamPage = lazyWithRetry(importMyTeamPage);
const importAvailableTeams = () => import("./components/AvailableTeams.jsx");
const AvailableTeams = lazyWithRetry(importAvailableTeams);
const importCreateTeam = () => import("./components/CreateTeam.jsx");
const CreateTeam = lazyWithRetry(importCreateTeam);
const importLiveSchedulePage = () => import("./components/schedule/LiveSchedulePage.jsx");
const LiveSchedulePage = lazyWithRetry(importLiveSchedulePage);
const StaffReimbursementsPage = lazyWithRetry(() => import("./components/StaffReimbursementsPage.jsx"));
const importReimbursementPortalPage = () => import("./components/ReimbursementPortalPage.jsx");
const ReimbursementPortalPage = lazyWithRetry(importReimbursementPortalPage);
// Pages nested under HackerDashboardLayout suspend twice on a cold load: React
// cannot render the page's lazy component (and so start its chunk download)
// until the layout chunk has downloaded and executed, serializing two network
// round trips. Firing the matched page's import up front lets both chunks
// download together; dynamic imports are deduped, so the lazy component picks
// up the same in-flight request. Keyed by exact path — these are the layout's
// children from the route table below.
const PORTAL_PAGE_IMPORTERS = {
  [HACKER_PORTAL_PATH]: importMyApplicationPage,
  "/qr-code": importQRCodePage,
  "/teams": importTeamsPage,
  "/my-team": importMyTeamPage,
  "/available-teams": importAvailableTeams,
  "/create-team": importCreateTeam,
  "/reimbursement": importReimbursementPortalPage,
  "/dashboard/credits": importSponsorPerksPage,
  "/dashboard/schedule": importLiveSchedulePage,
  "/dashboard/tracks": importTracksPage,
  "/dashboard/sponsors": importSponsorsPage,
};

// Fire it here, at module evaluation, not from an effect: on a cold load the
// first passive-effect flush lands a couple hundred milliseconds later (entry
// parse, telemetry init, first paint all contend for the main thread), which
// on the live site gave back most of the round trip this exists to save.
PORTAL_PAGE_IMPORTERS[window.location.pathname]?.().catch(() => {});

function PageLoader() {
  return (
    <div className="min-h-screen bg-[#0B0E34] flex items-center justify-center">
      <div
        className="w-10 h-10 border-4 rounded-full animate-spin"
        style={{ borderColor: "#00f0ff", borderTopColor: "transparent" }}
      />
    </div>
  );
}

function App() {
  const location = useLocation();
  // Route-level kill switch for the fixed Navbar; nothing sets it today.
  // Portal routes hide the Navbar separately via portalShell below.
  const hideNavbar = false;
  const toolShell =
    !hideNavbar && NAVBAR_TOOL_ROUTES.has(location.pathname);
  // Routes rendered inside HackerDashboardLayout: the sidebar is the only
  // chrome there (it carries site links and log out), so the Navbar does not
  // render at all.
  const portalShell =
    location.pathname === HACKER_PORTAL_PATH ||
    ["/qr-code", "/teams", "/my-team", "/available-teams", "/create-team", "/reimbursement"].includes(location.pathname) ||
    location.pathname.startsWith("/dashboard/");

  return (
    <div className={toolShell ? "min-h-screen flex flex-col" : undefined}>
      <UpdateNotice />
      <CommandPalette />
      {!hideNavbar && !portalShell && <Navbar layout={toolShell ? "flow" : "fixed"} />}
      <link
        rel="icon"
        type="image/svg+xml"
        href="/favicon.svg"
      ></link>
      <div
        id="main-content"
        className={
          toolShell
            ? "flex-1 min-h-0 min-w-0 overflow-x-hidden overflow-y-auto"
            : undefined
        }
      >
        <Suspense fallback={<PageLoader />}>
          <Routes>
          <Route path="/" element={<V2PreviewPage />} />
          <Route path="/register" element={<Navigate to="/apply" replace />} />
          <Route path="/registration" element={<Navigate to="/apply" replace />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/auth-callback" element={<AuthCallback />} />
          <Route path="/reset-password" element={<Navigate to="/login" replace />} />
          {/* /sponsors is the wall of who backed the event; /sponsorship is
              the pitch aimed at companies who have not yet. */}
          <Route path="/sponsors" element={<SponsorsPage />} />
          <Route path="/sponsorship" element={<SponsorshipPage />} />
          {/* All tracks as tiles with one detail panel below; the URL hash
              picks the track, so /tracks#x-tract deep links still work. */}
          <Route path="/tracks" element={<TracksPage />} />
          {/* The tile layout shipped from its experiment route; keep it working. */}
          <Route path="/tracksV2" element={<Navigate to="/tracks" replace />} />
          {/* Unlisted: only linked from the Career Center's employer invitation. */}
          <Route path="/career-fair-partners" element={<CareerFairPartnersPage />} />
          <Route path="/about" element={<NotFoundPage />} />

          <Route path="/staff/dashboard" element={<StaffDashboard />} />
          {/* Data Management now lives inside the console's Data tab. */}
          <Route path="/staff/database" element={<Navigate to="/staff/console?mode=data" replace />} />
          <Route path="/staff/scanner" element={<QRScanner />} />
          <Route path="/staff/teams" element={<StaffTeamsPage />} />
          <Route path="/staff/metrics" element={<StaffMetricsPage />} />
          <Route path="/staff/outbox" element={<StaffOutboxPage />} />
          <Route path="/staff/reimbursements" element={<StaffReimbursementsPage />} />
          <Route path="/csv" element={<CsvExportPage />} />
          <Route path="/export" element={<Navigate to="/csv" replace />} />
          <Route path="/csv-export" element={<Navigate to="/csv" replace />} />
          {/* Referral raffle draw sheet. Staff-gated like the rest of /staff,
              but deliberately not on the dashboard: it is a one-off draw, not
              a tool anyone needs during the event. Reachable by URL only. */}
          <Route path="/ref" element={<ReferrerLeaderboard />} />
          <Route path="/referrers" element={<Navigate to="/ref" replace />} />
          <Route path="/referrer" element={<Navigate to="/ref" replace />} />
          {/* The User console replaces Account Recovery; keep the old link working. */}
          <Route path="/staff/account-recovery" element={<Navigate to="/staff/console" replace />} />
          <Route path="/staff/console" element={<StaffConsole />} />
          <Route path="/judging" element={<JudgingPage />} />
          {/* The form and leaderboards merged into /judging; keep old links working. */}
          <Route path="/judging-form" element={<Navigate to="/judging" replace />} />
          <Route path="/judging-results" element={<Navigate to="/judging?view=leaderboard" replace />} />
          <Route path="/schedule" element={<LiveSchedulePage />} />
          {/* Registration is closed to the public: /apply is the notice. The
              form itself lives at an unlisted URL so staff can still add
              late Pitt/CMU hackers; it is deliberately linked from nowhere. */}
          <Route path="/apply" element={<ApplyClosedPage />} />
          <Route path="/applystaff" element={<ApplyPage />} />
          <Route path="/review" element={<ReviewPage />} />
          <Route path="/unauthorized" element={<UnauthorizedPage />} />
          <Route path="/my-application" element={<Navigate to={HACKER_PORTAL_PATH} replace />} />

          {/* Hacker Dashboard Portal Nested Routes */}
          <Route element={<HackerDashboardLayout />}>
            <Route path="/dashboard/credits" element={<SponsorPerksPage />} />
            {/* Pre-rename spelling; the page has always been called Credits. */}
            <Route path="/dashboard/perks" element={<Navigate to="/dashboard/credits" replace />} />
            <Route path={HACKER_PORTAL_PATH} element={<MyApplicationPage />} />
            <Route path="/qr-code" element={<QRCodePage />} />
            <Route path="/teams" element={<TeamsPage />} />
            <Route path="/my-team" element={<MyTeamPage />} />
            <Route path="/available-teams" element={<AvailableTeams />} />
            <Route path="/create-team" element={<CreateTeam />} />
            <Route path="/reimbursement" element={<ReimbursementPortalPage />} />
            <Route path="/dashboard/schedule" element={<LiveSchedulePage embedded />} />
            <Route path="/dashboard/tracks" element={<TracksPage embedded />} />
            <Route path="/dashboard/sponsors" element={<SponsorsPage embedded />} />
          </Route>

          <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
        {!hideNavbar && !["/login", "/auth-callback", "/unauthorized"].includes(location.pathname) && <Footer />}
      </div>
    </div>
  );
}

export default App;
